import React, { useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, TextField, Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useAiAccess from '../hooks/useAiAccess'
import useSnackbar from '../hooks/useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { budgetYearLastDate, budgetYearStartDate, noBudgetReason, refusalSentence, teamManagersPhrase } from '../utils/aiAccess'
import { formatCredits, formatGBP } from '../tokenbudgets/model'
import { AI_LEVELS, MAX_AI_LEVEL, MIN_AI_LEVEL, aiLevelLabel } from '../constants/aiLevels'
import type { AIAccessFeatureInfo } from '../types/gotypes'

// Level 1 first, rising to 4 — the order the levels build on each other
// (Adam, 25 Sep; #1293 first had 4 first).
const LEVELS_LOW_TO_HIGH = [MIN_AI_LEVEL, 2, 3, MAX_AI_LEVEL]

/**
 * "Your AI access" (#1537 PR D, issue #1293's sales surface). Every
 * signed-in customer user's own level, every level side by side with what it
 * adds, and a Request access button for anyone below the top. The
 * feature→level map comes from the server (useAiAccess) — nothing here is
 * written down twice.
 */
const AiAccessPage: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const customer = (router.params.customer as string) || account.customer?.name || ''
  const aiAccess = useAiAccess(customer)
  const snackbar = useSnackbar()

  const [message, setMessage] = useState('')
  const [lastRequestedAt, setLastRequestedAt] = useState<number | null>(null)
  // Distinct from "notified nobody" — a licensing outage while looking up
  // Realm admins must not read as "there are none" (#1537 PR D review, F4).
  const [notifyFailed, setNotifyFailed] = useState(false)

  const isRealmAdmin = !!account.user?.role_permissions?.isRealmAdmin

  const handleRequest = async () => {
    try {
      const res = await aiAccess.handleRequestAccess({ message: message.trim() || undefined })
      setLastRequestedAt(res.requested_at)
      setNotifyFailed(!!res.notify_failed)
      setMessage('')
      if (res.notify_failed) {
        snackbar.warning('Saved — but we could not reach the admin list to notify anyone. Contact Bayes Price if this keeps happening.')
      } else {
        snackbar.success(
          res.notified_admins > 0
            ? `Sent — ${res.notified_admins} ${res.notified_admins === 1 ? 'person' : 'people'} notified.`
            : 'Sent, but no Realm admin was found for this customer. Contact Bayes Price.'
        )
      }
    } catch (error: any) {
      const requestedAt = error?.response?.data?.requested_at
      if (typeof requestedAt === 'number') setLastRequestedAt(requestedAt)
      snackbar.error(extractErrorMessage(error))
    }
  }

  const level = aiAccess.level ?? MIN_AI_LEVEL
  const budget = aiAccess.budget
  // What the budget does now (only with enforcement on, when /me carries one).
  // A team with no budget has both kinds exhausted, so it reads as "interactive".
  const interactiveStopped = !!budget && budget.exhausted.interactive
  const batchOnlyStopped = !!budget && budget.exhausted.batch && !budget.exhausted.interactive
  const offLabel = (f: AIAccessFeatureInfo) =>
    budget && aiAccess.levelAllows(f.feature) && aiAccess.budgetExhausted(f.feature)
      ? (budget?.no_budget ? (budget.state === 'ended' ? 'Off: budget year ended' : budget.state === 'scheduled' ? 'Off: budget year not started' : 'Off: no budget') : 'Off: budget used')
      : null
  // A used budget never reads as a negative figure: it is "£0.00 left".
  const leftText = (b: NonNullable<typeof budget>) =>
    `${b.unit === 'credits' ? formatCredits(Math.max(0, b.remaining_credits)) : formatGBP(Math.max(0, b.remaining_gbp))} left in your team's AI budget`
  const featuresAtLevel = (target: number, features: AIAccessFeatureInfo[]) =>
    features.filter((f) => f.level === target)

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Your AI access</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isRealmAdmin
          ? 'What every level unlocks for your colleagues — empower them to do more from User management.'
          : `What your level unlocks, and what the next one adds. To access more, speak with ${teamManagersPhrase(aiAccess.teamManagers)}.`}
      </Typography>

      {!aiAccess.loaded ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          {!aiAccess.enforced && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Levels aren&apos;t enforced yet for this customer — everyone has full access, whatever level is shown below.
            </Alert>
          )}

          {interactiveStopped && (
            <Alert severity="error" sx={{ mb: 3 }} data-testid="ai-access-stopped">
              {refusalSentence({
                reason: budget?.no_budget ? noBudgetReason(budget.state) : 'budget_exhausted',
                admins: aiAccess.admins, team_managers: aiAccess.teamManagers,
                starts_at: budget?.starts_at, ends_at: budget?.ends_at,
              })}
            </Alert>
          )}
          {batchOnlyStopped && (
            <Alert severity="warning" sx={{ mb: 3 }} data-testid="ai-access-batch-stopped">
              Batch work (ThemeAI full runs, AutoCoder) is stopped: your team has used its budget. Chat, insights and agents continue for now, in your team's headroom.
            </Alert>
          )}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip color="info" label={`Level ${level} · ${aiLevelLabel(level)}`} />
                <Typography variant="body2" color="text.secondary">
                  {aiAccess.teamName ? `Team: ${aiAccess.teamName}` : 'No budget team'}
                </Typography>
                {budget && (budget.no_budget ? (
                  <Typography variant="body2" color="error.main" data-testid="ai-access-budget">
                    {budget.state === 'ended' && budget.ends_at != null
                      ? `Your team's AI budget year ended on ${budgetYearLastDate(budget.ends_at)}`
                      : budget.state === 'scheduled' && budget.starts_at != null
                        ? `Your team's AI budget year starts on ${budgetYearStartDate(budget.starts_at)}`
                        : 'Your team has no AI budget yet'}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" data-testid="ai-access-budget">
                    {leftText(budget)}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
            {LEVELS_LOW_TO_HIGH.map((tier) => {
              const isCurrent = tier === level
              const isAbove = level < tier
              const tierFeatures = featuresAtLevel(tier, aiAccess.features)
              return (
                <Card
                  key={tier}
                  variant="outlined"
                  data-testid={`ai-access-tier-${tier}`}
                  sx={{
                    flex: 1,
                    opacity: isAbove ? 0.55 : 1,
                    borderColor: isCurrent ? 'info.main' : 'divider',
                    borderWidth: isCurrent ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      {isCurrent && <CheckCircleOutlineIcon color="info" fontSize="small" />}
                      {isAbove && <LockOutlinedIcon fontSize="small" color="disabled" />}
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Level {tier} · {AI_LEVELS[tier]}
                      </Typography>
                    </Stack>
                    {tierFeatures.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {tier === MIN_AI_LEVEL ? 'Dashboards, tables and shared visuals — always open.' : 'Nothing new spends credits at this level.'}
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {tierFeatures.map((f) => {
                          const off = offLabel(f)
                          return (
                            <Typography key={f.feature} variant="body2" color={off ? 'text.secondary' : undefined} data-testid={`ai-access-feature-${f.feature}`}>
                              {f.label}
                              {off && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main', fontWeight: 600 }} data-testid="ai-access-feature-off">
                                  {off}
                                </Typography>
                              )}
                            </Typography>
                          )
                        })}
                      </Stack>
                    )}
                    {isAbove && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Speak with your team manager
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </Stack>

          {aiAccess.enforced && level < MAX_AI_LEVEL && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Request access</Typography>
                {lastRequestedAt ? (
                  <Typography variant="body2" color="text.secondary">
                    {notifyFailed
                      ? "Saved — we couldn't reach the admin list to notify anyone. Contact Bayes Price if this keeps happening."
                      : 'Sent — your team manager will see it in their notifications.'}
                  </Typography>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      placeholder="Optional — say what you need it for"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleRequest}
                      disabled={aiAccess.isRequestingAccess}
                      startIcon={aiAccess.isRequestingAccess ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                      Request access
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  )
}

export default AiAccessPage
